import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  changeLanguage,
  googleTranslateReady,
  initGoogleTranslate,
  reset,
} from 'volto-google-translate/actions';
import {
  GOOGLE_TRANSLATE_READY,
  googleTranslateElementWrapperId,
  ploneTranslateMapping,
  translateCookieName,
} from 'volto-google-translate/constants';
import {
  translateIsEnabled,
  useIsTranslating,
} from 'volto-google-translate/helpers';

import { BodyClass, langmap } from '@plone/volto/helpers';

import 'volto-google-translate/styles.css';

function GoogleTranslateHandler() {
  const dispatch = useDispatch();
  const shouldLoad = useSelector(
    (state) => !state.googleTranslate.loading && !state.googleTranslate.loaded,
  );
  const startLoad = useSelector(
    (state) =>
      state.loadProtector.requestCount === 0 &&
      state.loadProtector.isCounting === false,
  );
  // We'll use this to get the site languages without having a selector run every time
  const reduxStore = useStore();
  const translationActive = useIsTranslating();

  // Ensure translate cookie and redux are in-sync
  useEffect(() => {
    const cookie = document.cookie
      .split('; ')
      .find((cookieString) => cookieString.startsWith(translateCookieName));
    const googleSelectElement = document
      .getElementById(googleTranslateElementWrapperId)
      ?.querySelector('select');
    if (cookie && googleSelectElement && translationActive) {
      const cookieValueSplit = cookie.split('/');
      const language = cookieValueSplit[cookieValueSplit.length - 1];
      if (language) {
        if (langmap[language]) {
          dispatch(changeLanguage(language));
        } else {
          const state = reduxStore.getState();
          // Site languages used to ensure we match a mapping that is in-use
          let siteLanguages = [];
          if (state.siteInfo?.available_languages) {
            siteLanguages = state.siteInfo.available_languages;
          }
          // Volto 18 `@@site` endpoint support
          else if (state.site?.data?.['plone.available_languages']) {
            siteLanguages = state.site.data['plone.available_languages'];
          }
          const ploneLanguage =
            Object.keys(ploneTranslateMapping).find(
              (ploneLang) =>
                language === ploneTranslateMapping[ploneLang] &&
                siteLanguages.includes(ploneLang),
            ) || '';

          dispatch(changeLanguage(ploneLanguage));
        }
      } else {
        dispatch(reset());
      }
    }
  }, [dispatch, translationActive, reduxStore]);

  useEffect(() => {
    if (shouldLoad && startLoad) {
      dispatch(initGoogleTranslate());
    }
  }, [dispatch, shouldLoad, startLoad]);
  // Prepare the listener for the script loading to finish to bring us back to React land.
  //   Hacky but works.
  useEffect(() => {
    function dispatchTranslateReady() {
      dispatch(googleTranslateReady());
    }
    document.addEventListener(GOOGLE_TRANSLATE_READY, dispatchTranslateReady);
    return () => {
      document.removeEventListener(
        GOOGLE_TRANSLATE_READY,
        dispatchTranslateReady,
      );
    };
  }, [dispatch]);

  return (
    <BodyClass className={translationActive ? `translation-active` : null} />
  );
}

export function GoogleTranslateInit() {
  if (!translateIsEnabled()) {
    return null;
  }
  return <GoogleTranslateHandler />;
}
